import { isValidBlockSubtypeSlug } from './blockSubtype.types';

/**
 * UXR C7 — operator-selected "available blocks" restriction for strategy
 * generation. Absent on a request = no restriction (prompts stay byte-identical
 * to the unrestricted contract). Present = a complete whitelist: the strategist
 * may emit only listed blockTypes, and only listed customSubtype slugs.
 */

/** Canonical built-in strategy block types (mirrors StrategyBlock['blockType']). */
export const STRATEGY_BLOCK_TYPE_CATALOG = [
  'welcome',
  'user_goals',
  'user_data',
  'plan_paywall',
  'history',
  'motivation',
  'environment',
  'habits',
  'strategy',
  'custom',
] as const;

export type StrategyBlockTypeName = typeof STRATEGY_BLOCK_TYPE_CATALOG[number];

/**
 * Structural types every generated strategy needs (fixed welcome/goals entry +
 * per-task paywall close). They are force-included server-side so a restricted
 * selection can never produce a structurally invalid strategy.
 */
export const STRATEGY_STRUCTURAL_BLOCK_TYPES: StrategyBlockTypeName[] = [
  'welcome',
  'user_goals',
  'plan_paywall',
];

export const STRATEGY_AVAILABLE_SUBTYPE_LIMIT = 30;

export interface StrategyAvailableBlocks {
  /** Allowed built-in block types (whitelist). Never empty after normalization. */
  blockTypes: string[];
  /** Allowed project subtype slugs (whitelist). Empty = no subtypes allowed. */
  subtypeSlugs: string[];
}

export class StrategyAvailableBlocksValidationError extends Error {
  readonly code = 'invalid_available_blocks';

  constructor(readonly field: string, message: string) {
    super(message);
    this.name = 'StrategyAvailableBlocksValidationError';
  }
}

/**
 * Normalize the caller payload. Returns undefined for an absent restriction so
 * the unrestricted path renders byte-identically. Throws on malformed input.
 */
export function normalizeStrategyAvailableBlocks(
  value: unknown,
): StrategyAvailableBlocks | undefined {
  if (value === undefined || value === null) return undefined;
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw invalid('availableBlocks', 'availableBlocks must be an object with blockTypes and subtypeSlugs arrays.');
  }
  const record = value as Record<string, unknown>;

  if (!Array.isArray(record.blockTypes)) {
    throw invalid('availableBlocks.blockTypes', 'availableBlocks.blockTypes must be an array of block type names.');
  }
  const knownTypes = new Set<string>(STRATEGY_BLOCK_TYPE_CATALOG);
  const blockTypes = new Set<string>();
  for (const entry of record.blockTypes) {
    if (typeof entry !== 'string' || !entry.trim()) {
      throw invalid('availableBlocks.blockTypes', 'availableBlocks.blockTypes entries must be non-empty strings.');
    }
    const normalized = entry.trim();
    if (!knownTypes.has(normalized)) {
      throw invalid(
        'availableBlocks.blockTypes',
        `Unknown block type "${normalized}". Known types: ${STRATEGY_BLOCK_TYPE_CATALOG.join(', ')}.`,
      );
    }
    blockTypes.add(normalized);
  }
  if (blockTypes.size === 0) {
    throw invalid('availableBlocks.blockTypes', 'availableBlocks.blockTypes must contain at least one block type.');
  }

  const rawSlugs = record.subtypeSlugs ?? [];
  if (!Array.isArray(rawSlugs)) {
    throw invalid('availableBlocks.subtypeSlugs', 'availableBlocks.subtypeSlugs must be an array of subtype slugs.');
  }
  if (rawSlugs.length > STRATEGY_AVAILABLE_SUBTYPE_LIMIT) {
    throw invalid(
      'availableBlocks.subtypeSlugs',
      `availableBlocks.subtypeSlugs must contain at most ${STRATEGY_AVAILABLE_SUBTYPE_LIMIT} entries.`,
    );
  }
  const subtypeSlugs = new Set<string>();
  for (const entry of rawSlugs) {
    if (!isValidBlockSubtypeSlug(entry)) {
      throw invalid('availableBlocks.subtypeSlugs', 'availableBlocks.subtypeSlugs entries must be normalized slugs.');
    }
    subtypeSlugs.add(entry);
  }

  for (const structural of STRATEGY_STRUCTURAL_BLOCK_TYPES) blockTypes.add(structural);
  // A subtype can only ride on blockType "custom" — an allowed subtype implies it.
  if (subtypeSlugs.size > 0) blockTypes.add('custom');

  return {
    blockTypes: [...blockTypes].sort(),
    subtypeSlugs: [...subtypeSlugs].sort(),
  };
}

export class StrategyBlockAvailabilityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StrategyBlockAvailabilityError';
  }
}

/**
 * Server-side backstop behind the bounded prompt section: any emitted block
 * outside the operator-allowed set is rejected (same retry rail as unknown
 * customSubtype slugs — the message carries the "operator-allowed" marker).
 */
export function assertStrategyBlocksWithinAvailability<T>(
  blocks: T[],
  availability: StrategyAvailableBlocks | undefined,
): T[] {
  if (!availability) return blocks;
  const allowedTypes = new Set(availability.blockTypes);
  const allowedSlugs = new Set(availability.subtypeSlugs);
  const violations: string[] = [];
  blocks.forEach((value, index) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return;
    const block = value as Record<string, unknown>;
    const id = typeof block.entityId === 'string' && block.entityId.trim()
      ? block.entityId
      : typeof block.id === 'string' && block.id.trim() ? block.id : `at index ${index}`;
    const blockType = typeof block.blockType === 'string' ? block.blockType : '';
    if (!allowedTypes.has(blockType)) {
      violations.push(`Block "${id}" uses blockType "${blockType || 'missing'}" outside the operator-allowed set.`);
    }
    if (typeof block.customSubtype === 'string' && block.customSubtype.trim()
      && !allowedSlugs.has(block.customSubtype.trim())) {
      violations.push(`Block "${id}" uses customSubtype "${block.customSubtype.trim()}" outside the operator-allowed set.`);
    }
  });
  if (violations.length > 0) {
    throw new StrategyBlockAvailabilityError(
      `${violations.join(' ')} Allowed block types: ${availability.blockTypes.join(', ')}. `
      + `Allowed custom subtypes: ${availability.subtypeSlugs.join(', ') || 'none'}.`,
    );
  }
  return blocks;
}

function invalid(field: string, message: string): StrategyAvailableBlocksValidationError {
  return new StrategyAvailableBlocksValidationError(field, message);
}
